import toursCatalog from './tours.json';
import { ChecklistItemId } from '../services/onboardingService';
import type { UserRoleSlug } from '../auth/roles';

/**
 * A tour's audience is a ROLE, so this IS the role vocabulary — it was a
 * hand-typed copy of it until P5, which is exactly how a new role gets added to
 * two unions and forgotten in the third. `import type` is erased, and
 * `auth/roles.ts` imports nothing as a value, so the onboarding graph still pulls
 * in nothing from `auth/`.
 *
 * ⚠ TOTALITY: `tours.test.ts` keys its expected first-login table on this union,
 * so a role with no decision recorded is a compile error there. CSR's decision is
 * "no tour yet" (P5 design §E) — `pickTourForRole` already answers null for a role
 * no catalog entry claims, so nothing here needed a branch.
 */
export type TourRole = UserRoleSlug;

/**
 * Maps checklist item IDs to a mini-tour to fire on Start click.
 *
 * Deliberately empty for now — checklist items just navigate to the target
 * page. Page-level teaching is better served by dedicated empty-state
 * coaches and hotspots (Phase 3) than modal tour overlays.
 *
 * Re-add entries here when a genuinely non-obvious multi-step flow benefits
 * from a guided walkthrough (e.g., first SOAP note, Stripe Connect onboarding).
 */
export const CHECKLIST_TO_TOUR: Partial<Record<ChecklistItemId, string>> = {};

export type TourPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end'
  | 'auto'
  | 'center';

export interface TourStepDefinition {
  id: string;
  target: string;
  title: string;
  body: string;
  placement?: TourPlacement;
}

export interface TourDefinition {
  id: string;
  version: number;
  roles: TourRole[];
  trigger: 'first_login' | 'manual';
  enabled: boolean;
  title: string;
  steps: TourStepDefinition[];
}

interface ToursCatalog {
  tours: TourDefinition[];
}

const catalog = toursCatalog as ToursCatalog;

export const TOURS: TourDefinition[] = catalog.tours;

export function getTourById(id: string): TourDefinition | undefined {
  return TOURS.find((tour) => tour.id === id && tour.enabled);
}

export function pickTourForRole(role: TourRole | string | undefined): TourDefinition | null {
  if (!role) return null;
  const match = TOURS.find(
    (tour) => tour.enabled && tour.trigger === 'first_login' && tour.roles.includes(role as TourRole),
  );
  return match ?? null;
}
